<template>
  <div>
    <b-row v-for="{ key, title, total, components } in statBlocks.filter(({ total: t }) => t && t.COUNT)" :key="key" class="py-3 border-top">
      <b-col>
        <div class="d-flex align-items-middle justify-content-between">
          <div>
            <p class="mb-0">
              <span><span class="mr-2" style="font-size: 22px;"><strong>{{ total.COUNT }}</strong></span></span>
              <span>(<strong>{{ components.reduce((r, e) =>  r + (e.points || 0) ,0) }}</strong> points awarded)</span>
            </p>
            <h4><strong>{{ title }}</strong></h4>
          </div>
          <div>
            <b-button v-if="components.length > 1" variant="outline-primary" v-b-toggle="`collapse-${key}`" size="sm" class="m-1">Details</b-button>
          </div>
        </div>
        <div v-if="components.length > 1">
          <b-collapse :id="`collapse-${key}`">
            <b-card>
              <div v-for="{ title, count, points } in components" :key="title" class="py-2">
                <strong>{{ count }}</strong> ({{ points }} points awarded)<br/>
                <span>{{ title }}</span>
              </div>
            </b-card>
          </b-collapse>
        </div>
      </b-col>
    </b-row>
  </div>
</template>

<script>
export default {
  props: {
    stats: {
      type: Object,
      default: null,
    },
  },
  computed: {
    statBlocks() {
      const { stats, upsertBlock } = this;
      const blocks = Object.keys(stats).reduce((r, key) => {
        const [ eventName ] = key.split('::');
        if ([ 'JOB_SHARED', 'REFERRAL_SUGGESTED', 'EVENT_REFERRAL_CREATED', 'JOB_BROWSE_LINKEDIN' ].includes(eventName)) {
          return upsertBlock(r, key, { key: 'job-shares', title: '📤 Job Shares', position: 1 });
        }
        if ([ 'SHARE_VISITED', 'SHARE_UNIQUE_VISITED', 'REFERRAL_VISITED', 'REFERRAL_UNIQUE_VISITED' ].includes(eventName)) {
          return upsertBlock(r, key, { key: 'job-views', title: '🤩 Job Views', position: 2 });
        }
        if ([ 'CONTENT_SHARED', 'CONTENT_SCHEDULE_SHARE_FIRST' ].includes(eventName)) {
          return upsertBlock(r, key, { key: 'content-shares', title: '📤 Content Shares', position: 3 });
        }
        if ([ 'CONTENT_SHARE_VISITED', 'CONTENT_SHARE_UNIQUE_VISITED' ].includes(eventName)) {
          return upsertBlock(r, key, { key: 'content-views', title: '🤩 Content Views', position: 4 });
        }
        if ([ 'APPLICATION_CREATED', 'APPLICATION_CONFIRMED' ].includes(eventName)) {
          return upsertBlock(r, key, { key: 'submitted-applications', title: '📄 Submitted Applications', position: 5 });
        }
        if ([ 'APPLICATION_INTERVIEW' ].includes(eventName)) {
          return upsertBlock(r, key, { key: 'interviews', title: '📝 Interviews', position: 6 });
        }
        if ([ 'APPLICATION_HIRED' ].includes(eventName)) {
          return upsertBlock(r, key, { key: 'hires', title: '🤝 Hires', position: 7 });
        }
        return r;
      }, []).sort((a, b) => a.position - b.position);
      return blocks;
    },
    eventMap: () => ({
      APPLICATION_CONFIRMED: 'Application confirmed',
      APPLICATION_CREATED: 'Application created',
      APPLICATION_HIRED: 'Application hired',
      APPLICATION_INTERVIEW: 'Application interview',
      CONTENT_SCHEDULE_SHARE_FIRST: 'Content scheduled on LinkedIn',
      CONTENT_SHARE_FIRST_VISITED: 'Content visited for the first time',
      CONTENT_SHARE_UNIQUE_VISITED: 'Content visited by a new visitor',
      CONTENT_SHARE_VISITED: 'Content visited',
      CONTENT_SHARED: 'Copy content link',
      EVENT_REFERRAL_CREATED: 'Direct email referral in a jam',
      JOB_BROWSE_LINKEDIN: 'Browse LinkedIn connections',
      JOB_SCHEDULE_SHARE_FIRST: 'Job scheduled on LinkedIn',
      JOB_SHARED: 'Copy job link',
      REFERRAL_FIRST_VISITED: 'Referral visited for the first time',
      REFERRAL_SUGGESTED: 'Direct email referral',
      REFERRAL_UNIQUE_VISITED: 'Referral visited by a new visitor',
      REFERRAL_VISITED: 'Referral visited',
      SHARE_FIRST_VISITED: 'Job visited for the first time',
      SHARE_UNIQUE_VISITED: 'Job visited by a new visitor',
      SHARE_VISITED: 'Job visited',
    })
  },
  methods: {
    upsertBlock(r, key, blockDetails) {
      const [ eventName, type, period ] = key.split('::');
      const { stats, eventMap } = this;
      const block = r.find(block => block.key === blockDetails.key);
      if (!block) {
        const value = typeof stats[key] === 'string' ? parseInt(stats[key]) : stats[key];
        const item = {
          key: blockDetails.key,
          title: blockDetails.title,
          position: blockDetails.position,
          total: {},
          components: [
            {
              key: eventName,
              title: eventMap[eventName],
              count: type === 'COUNT' ? value || 0 : 0,
              points: type === 'POINTS' ? value || 0 : 0,
            }
          ]
        };
        if (![
          'APPLICATION_CONFIRMED',
          'CONTENT_SHARE_FIRST_VISITED',
          'CONTENT_SHARE_UNIQUE_VISITED',
          'SHARE_FIRST_VISITED',
          'SHARE_UNIQUE_VISITED',
          'REFERRAL_FIRST_VISITED',
          'REFERRAL_UNIQUE_VISITED'
        ].includes(eventName)) {
          item.total[`${type || ''}${period ? '_' : ''}${period || ''}`] = value || 0;
        }
        r.push(item);
      } else {
        const component = block.components.find(c => c.key === eventName);
        const value = typeof stats[key] === 'string' ? parseInt(stats[key]) : stats[key];

        if (![
          'APPLICATION_CONFIRMED',
          'CONTENT_SHARE_FIRST_VISITED',
          'CONTENT_SHARE_UNIQUE_VISITED',
          'SHARE_FIRST_VISITED',
          'SHARE_UNIQUE_VISITED',
          'REFERRAL_FIRST_VISITED',
          'REFERRAL_UNIQUE_VISITED'
        ].includes(eventName)) {
          block.total[`${type || ''}${period ? '_' : ''}${period || ''}`] = (block.total[`${type || ''}${period ? '_' : ''}${period || ''}`] || 0) + (value || 0);
        }

        if (!component) {
          block.components.push({
            key: eventName,
            title: eventMap[eventName],
            count: type === 'COUNT' ? value || 0 : 0,
            points: type === 'POINTS' ? value || 0 : 0,
          });
        } else {
          component.count = type === 'COUNT' ? (value || 0) : (component.count || 0);
          component.points = type === 'POINTS' ? (value || 0) : (component.points || 0);
        }
      }
      return r;
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/campaign-admin/CampaignHistoryStatsBlock.vue